import { useEffect, useRef, useCallback } from "react";

interface Orb {
  x: number;
  y: number;
  targetX: number;
  targetY: number;
  size: number;
  color: string;
  speed: number;
  offsetX: number;
  offsetY: number;
}

export const InteractiveBackground = () => {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const mousePos = useRef({ x: 0, y: 0 });
  const orbs = useRef<Orb[]>([]);
  const animationFrameId = useRef<number>();
  const isInitialized = useRef(false);

  const initOrbs = useCallback((width: number, height: number) => {
    const orbConfigs = [
      { size: 400, color: "rgba(147, 197, 253, 0.15)", speed: 0.02 },
      { size: 350, color: "rgba(203, 213, 225, 0.12)", speed: 0.025 },
      { size: 300, color: "rgba(191, 219, 254, 0.1)", speed: 0.03 },
      { size: 450, color: "rgba(226, 232, 240, 0.1)", speed: 0.015 },
      { size: 280, color: "rgba(165, 180, 252, 0.12)", speed: 0.035 },
    ];

    mousePos.current = { x: width / 2, y: height / 2 };

    return orbConfigs.map((config, i) => ({
      x: width / 2 + (i - 2) * 150,
      y: height / 2,
      targetX: width / 2,
      targetY: height / 2,
      size: config.size,
      color: config.color,
      speed: config.speed,
      offsetX: (i - 2) * 80,
      offsetY: (i % 2 === 0 ? -1 : 1) * 50,
    }));
  }, []);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;

    const ctx = canvas.getContext("2d", { alpha: true });
    if (!ctx) return;

    let width = window.innerWidth;
    let height = window.innerHeight;

    const resizeCanvas = () => {
      width = window.innerWidth;
      height = window.innerHeight;
      canvas.width = width;
      canvas.height = height;
      
      if (!isInitialized.current) {
        orbs.current = initOrbs(width, height);
        isInitialized.current = true;
      }
    };
    
    resizeCanvas();
    window.addEventListener("resize", resizeCanvas);

    const handleMouseMove = (e: MouseEvent) => {
      mousePos.current = { x: e.clientX, y: e.clientY };
    };
    window.addEventListener("mousemove", handleMouseMove, { passive: true });

    const animate = () => {
      ctx.clearRect(0, 0, width, height);

      orbs.current.forEach((orb) => {
        // Use fixed offsets instead of random per frame
        orb.targetX = mousePos.current.x + orb.offsetX;
        orb.targetY = mousePos.current.y + orb.offsetY;

        // Smoothly interpolate
        orb.x += (orb.targetX - orb.x) * orb.speed;
        orb.y += (orb.targetY - orb.y) * orb.speed;

        // Draw soft gradient orb (no CSS blur needed)
        const gradient = ctx.createRadialGradient(
          orb.x, orb.y, 0,
          orb.x, orb.y, orb.size
        );
        gradient.addColorStop(0, orb.color);
        gradient.addColorStop(0.5, orb.color.replace(/[\d.]+\)$/, "0.05)"));
        gradient.addColorStop(1, "rgba(0, 0, 0, 0)");

        ctx.fillStyle = gradient;
        ctx.beginPath();
        ctx.arc(orb.x, orb.y, orb.size, 0, Math.PI * 2);
        ctx.fill();
      });

      animationFrameId.current = requestAnimationFrame(animate);
    };

    animate();

    return () => {
      window.removeEventListener("resize", resizeCanvas);
      window.removeEventListener("mousemove", handleMouseMove);
      if (animationFrameId.current) {
        cancelAnimationFrame(animationFrameId.current);
      }
    };
  }, [initOrbs]);

  return (
    <canvas
      ref={canvasRef}
      className="absolute inset-0 pointer-events-none"
      style={{ zIndex: 1 }}
    />
  );
};
